import { liteClient as algoliasearch } from "algoliasearch/lite";
import instantsearch from "instantsearch.js";
import { configure } from "instantsearch.js/es/widgets";

// Private module-level variables
let algoliaData = null;
let client = null;
let instantSearchInstance = null;
let initPromise = null;

/**
 * Initializes the Algolia client and InstantSearch instance
 * @returns {Promise<void>}
 */
export async function initAlgolia() {
	// If already initialized, return early
	if (client) {
		return;
	}

	// If initialization is in progress, wait for it to complete
	if (initPromise) {
		return initPromise;
	}

	// Start new initialization
	initPromise = (async () => {
		try {
			algoliaData = initAlgoliaBaseData();

			algoliaData = {
				...algoliaData,
				...(await getAlgoliaCredentials()),
			};

			client = algoliasearch(
				algoliaData.applicationId,
				algoliaData.securedApiKey,
			);

			instantSearchInstance = instantsearch({
				indexName: algoliaData.indexName,
				future: {
					preserveSharedStateOnUnmount: true,
				},
				searchClient: client,
			});
		} catch (error) {
			// Clear the initialization state on error
			client = null;
			instantSearchInstance = null;
			throw error;
		} finally {
			// Always reset the promise when done (success or failure)
			initPromise = null;
		}
	})();

	return initPromise;
}

/**
 * Initializes and retrieves the Algolia base data from the DOM.
 * @returns {object} The parsed Algolia base data.
 * @throws {Error} If the Algolia base data is not found or cannot be parsed.
 * @example
 * // Example of the data returned:
 * {
 *   "indexName": "finstral_extranet_stage",
 *   "langCode": "en",
 * }
 */
function initAlgoliaBaseData() {
	const algoliaBaseData = document.querySelector("#algolia-data");

	if (!algoliaBaseData) {
		throw new Error("Algolia base data not found.");
	}

	try {
		const algoliaBaseDataJson = algoliaBaseData.innerHTML;
		const parsedAlgoliaBaseData = JSON.parse(algoliaBaseDataJson);

		setAlgoliaBaseDataInStorage(parsedAlgoliaBaseData);

		return parsedAlgoliaBaseData;
	} catch (error) {
		throw new Error(
			`Failed to parse or store Algolia base data: ${error.message}`,
		);
	}
}

/**
 * Stores the provided Algolia base data in local storage.
 * @param {object} data - The Algolia base data to store.
 * @throws {Error} If the data cannot be stored in local storage.
 */
function setAlgoliaBaseDataInStorage(data) {
	try {
		localStorage.setItem("algoliaBaseData", JSON.stringify(data));
	} catch (error) {
		throw new Error(
			`Failed to set Algolia base data in storage: ${error.message}`,
		);
	}
}

/**
 * Retrieves the Algolia base data from local storage.
 * @returns {object} The parsed Algolia base data.
 * @throws {Error} If the data is not found or cannot be parsed.
 */
export function getAlgoliaBaseDataFromStorage() {
	const algoliaBaseData = localStorage.getItem("algoliaBaseData");

	if (!algoliaBaseData) {
		throw new Error(
			"Algolia base data not found. Algolia might not be initialized.",
		);
	}

	try {
		return JSON.parse(algoliaBaseData);
	} catch (error) {
		throw new Error(
			`Failed to parse Algolia base data from storage: ${error.message}`,
		);
	}
}

/**
 * Removes the Algolia base data from local storage.
 * @throws {Error} If the data cannot be removed from local storage.
 */
export function removeAlgoliaBaseDataFromStorage() {
	try {
		localStorage.removeItem("algoliaBaseData");
	} catch (error) {
		throw new Error(
			`Failed to remove Algolia base data from storage: ${error.message}`,
		);
	}
}

/**
 * Validates the provided Algolia API key by making a request to the server. If no API key
 * is provided, it returns 0.
 * https://factorial-io.atlassian.net/wiki/spaces/FINSTRAL/pages/10307829761/Search+API+Keys
 * @param {string} apiKey - The API key to validate.
 * @returns {Promise<object>} The response data indicating the time (in seconds) until the key expires.
 * @throws {Error} If the validation request fails or the server returns an error.
 */
export async function isApiKeyValid(apiKey) {
	// If no API key is provided, return 0 (indicating the key is invalid)
	// This is a fallback to avoid unnecessary API calls and to ensure
	// that the function can be called without an API key. This can
	// happen if the Algolia client is not initialized yet.
	if (!apiKey) {
		return Promise.resolve(0);
	}

	const validateApiKeyEndpoint = `/api/search/validate-key/${apiKey}`;

	try {
		const response = await fetch(validateApiKeyEndpoint);

		if (!response.ok) {
			throw new Error(`HTTP error: ${response.status}`);
		}

		const responseData = await response.json();

		return responseData;
	} catch (error) {
		throw new Error(`Failed to validate Algolia key: ${error.message}`);
	}
}

/**
 * Fetches Algolia credentials from the server
 * @returns {Promise<{applicationId: string, securedApiKey: string}>}
 */
async function getAlgoliaCredentials() {
	const generateKeyEndpoint = `/api/search/generate-key/${algoliaData.indexName}`;

	try {
		const response = await fetch(generateKeyEndpoint);

		if (!response.ok) {
			throw new Error(`HTTP error: ${response.status}`);
		}

		const responseData = await response.json();

		if (responseData && responseData.status === "error") {
			throw new Error(responseData.message);
		}

		return {
			applicationId: responseData.applicationId,
			securedApiKey: responseData.securedApiKey,
		};
	} catch (error) {
		throw new Error(`Failed to generate Algolia key: ${error.message}`);
	}
}

/**
 * Gets the Algolia client, initializing it if necessary
 * @returns {Promise<object>} The Algolia client
 */
export async function getAlgoliaClient() {
	if (!client) {
		await initAlgolia();
	}
	return client;
}

/**
 * Gets the InstantSearch instance, initializing it if necessary.
 * @returns {Promise<object>} The InstantSearch instance
 */
export async function getInstantSearchClient() {
	if (!instantSearchInstance) {
		await initAlgolia();
	}
	return instantSearchInstance;
}

/**
 * Retrieves the current Algolia index name.
 * @returns {string} The current Algolia index name.
 */
export function getCurrentAlgoliaIndexName() {
	if (algoliaData.indexName) {
		return algoliaData.indexName;
	}

	const algoliaBaseData = getAlgoliaBaseDataFromStorage();
	return algoliaBaseData.indexName;
}

/**
 * Retrieves the current Algolia language code.
 * @returns {string} The current Algolia language code.
 */
export function getCurrentAlgoliaLangCode() {
	if (algoliaData.langCode) {
		return algoliaData.langCode;
	}

	const algoliaBaseData = getAlgoliaBaseDataFromStorage();
	return algoliaBaseData.langCode;
}

/**
 * @typedef {'changed_asc' | 'changed_desc' | 'title_asc' | 'title_desc'} IndexType
 * Represents the possible Algolia sorting replica index types.
 */

/**
 * Retrieves the Algolia index name based on the specified type.
 * @param {IndexType} [type] - The sorting type to append to the base index name. Defaults to the primary index if not provided.
 * @returns {string} The full Algolia index name.
 */
export function getAlgoliaIndexNameByType(type) {
	const base = getCurrentAlgoliaIndexName();
	return type ? `${base}_${type}` : base;
}

/**
 * Constructs the filter string for Algolia search queries.
 * @returns {string} The filter string for Algolia queries.
 */
function getFilters() {
	return `search_api_language:${getCurrentAlgoliaLangCode()} AND NOT is_archived:true AND NOT is_secondary_variant:true`;
}

/**
 * Configures the search parameters for Algolia.
 * @param {string} [customFilters] - Additional custom filters to apply to the search query.
 * @param {number} [hitsPerPage] - The number of hits to display per page. (default: 9)
 * @returns {object} The configured search parameters.
 */
export function getAlgoliaSearchParametersConfiguration(
	customFilters,
	hitsPerPage = 9,
) {
	let filters = getFilters();

	if (customFilters) {
		filters = `${filters} AND ${customFilters}`;
	}

	return configure({
		hitsPerPage: hitsPerPage,
		filters,
	});
}

/**
 * Fetches the latest assets from the specified Algolia index
 * @param {number} [hitsPerPage] - The number of hits per page (default: 12)
 * @param {string} [query] - The search query (default: empty string)
 * @returns {Promise<Array>} An array of hits
 */
export async function getLatestAssets(hitsPerPage = 12, query = "") {
	const algoliaClient = await getAlgoliaClient();
	const indexName = getAlgoliaIndexNameByType("changed_desc");

	const { results } = await algoliaClient.search({
		requests: [
			{
				filters: getFilters(),
				hitsPerPage,
				indexName,
				query,
			},
		],
	});

	return results.length ? results[0].hits : [];
}
